import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Phone, 
  ShoppingCart, 
  CheckCircle, 
  XCircle, 
  Clock,
  TrendingUp,
  RefreshCw,
  Users
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card className="border-none shadow-sm">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Reorders() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/rts/stats/reorders`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching re-order stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reorders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Re-orders Tracking</h1>
          <p className="text-gray-500 mt-1">Monitor customer callbacks at re-orders</p>
        </div>
        <Button variant="outline" onClick={fetchStats}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard
          title="Total RTS"
          value={stats?.summary?.total_rts || 0}
          icon={Users}
          color="bg-gray-600"
        />
        <StatCard
          title="Pending Calls"
          value={stats?.summary?.total_pending || 0}
          icon={Clock}
          color="bg-yellow-500"
        />
        <StatCard
          title="Tinawagan"
          value={stats?.summary?.total_called || 0}
          icon={Phone}
          color="bg-blue-500"
        />
        <StatCard
          title="Nag Re-order"
          value={stats?.summary?.total_reorders || 0}
          icon={ShoppingCart}
          color="bg-green-600"
        />
        <StatCard
          title="Hindi Na-contact"
          value={stats?.summary?.total_not_contacted || 0}
          icon={XCircle}
          color="bg-orange-500"
        />
        <StatCard
          title="Ayaw Re-order"
          value={stats?.summary?.total_declined || 0}
          icon={XCircle}
          color="bg-red-500"
        />
        <StatCard
          title="Success Rate"
          value={`${stats?.summary?.success_rate || 0}%`}
          icon={TrendingUp}
          color="bg-green-600"
          subtitle="Re-order rate"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Calls */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-yellow-500" />
              Pending - Kailangan Tawagan ({stats?.pending_calls?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.pending_calls?.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats.pending_calls.map((record) => (
                  <div 
                    key={record.id} 
                    className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-100"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{record.customer_name}</p>
                      <p className="text-sm text-gray-600">{record.contact_number || "No contact"}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {record.tracking_number} • {record.date_ordered}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        {record.orders?.map(o => o.item_name).join(", ").substring(0, 30)}...
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>Lahat na tinawagan! 🎉</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Re-orders */}
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShoppingCart className="w-5 h-5 text-green-600" />
              Recent Re-orders ({stats?.recent_reorders?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_reorders?.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats.recent_reorders.map((record) => (
                  <div 
                    key={record.id} 
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{record.customer_name}</p>
                      <p className="text-sm text-gray-600">{record.contact_number}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Original: {record.tracking_number}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-green-100 text-green-800">Nag Re-order</Badge>
                      <p className="text-xs text-green-600 mt-1">
                        📅 {record.reorder_date}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {record.reorder_items?.map(o => `${o.item_name} x${o.quantity}`).join(", ").substring(0, 40)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Wala pang re-orders</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Re-orders Table */}
      {stats?.recent_reorders?.length > 0 && (
        <Card className="border-none shadow-sm">
          <CardHeader>
            <CardTitle>All Re-orders Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Original Order</TableHead>
                  <TableHead>Re-order Date</TableHead>
                  <TableHead>Re-order Items</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.recent_reorders.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <p className="font-medium">{record.customer_name}</p>
                      <p className="text-sm text-gray-500">{record.contact_number}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-mono text-sm">{record.tracking_number}</p>
                      <p className="text-xs text-gray-400">{record.date_ordered}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800">{record.reorder_date}</Badge>
                    </TableCell>
                    <TableCell>
                      {record.reorder_items?.map((item, i) => (
                        <p key={i} className="text-sm">{item.item_name} x{item.quantity}</p>
                      ))}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600 max-w-[200px] truncate">
                        {record.reorder_notes || record.call_notes || "-"}
                      </p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
